import React, { useMemo } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Modal from 'src/components/modal/Modal';
import { Grid } from '@mui/material';
import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import ApiError from 'src/api/ApiError';
import DataTable from 'src/md-examples/Tables/DataTable';
import { Column } from 'react-table';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  lineItem: LineItem;
  isLoading?: boolean;
  error?: ApiError;
}

const DeleteLineItemModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  lineItem,
  isLoading,
  error,
}) => {
  // Column definitions matching AssociateLineItemModal
  const lineItemColumns: Column<LineItem>[] = useMemo(
    () => [
      {
        Header: 'SKU',
        accessor: 'sku',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original;
          // Try all possible identifier fields in order of preference
          const identifier = item.customerIdentifier || item.sku || item.upc;

          return identifier ? (
            <MDTypography variant="caption">{identifier}</MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          );
        },
      },
      {
        Header: 'Item',
        accessor: 'itemName',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original;

          // Try all possible descriptive fields in order of preference
          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            'No description available';

          return (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="caption" color="text.secondary">
                {description}
              </MDTypography>
            </MDBox>
          );
        },
      },
      { Header: 'Quantity', accessor: 'quantity' },
      {
        Header: 'Unit Price',
        accessor: 'unitPrice',
        Cell: ({ value }: { value?: number | null }) => {
          return <>{value ? `$${value.toFixed(2)}` : ''}</>;
        },
      },
      {
        Header: 'Total',
        accessor: 'amount',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const original = row.original;
          return (
            <>
              {original.quantity && original.unitPrice
                ? `$${(
                    (original.quantity || 0) * (original.unitPrice || 0)
                  ).toFixed(2)}`
                : ''}
            </>
          );
        },
      },
    ],
    [],
  );

  // Memoize the table data to prevent unnecessary re-renders
  const tableData = useMemo(
    () => ({
      columns: lineItemColumns,
      rows: [lineItem],
    }),
    [lineItem, lineItemColumns],
  );

  return (
    <Modal
      open={open}
      title="Delete Line Item"
      closeable
      isLoading={isLoading}
      error={error}
      onCloseButton={{
        text: 'Cancel',
        onClick: onClose,
        disabled: isLoading,
      }}
      onConfirmButton={{
        text: 'Delete',
        onClick: onConfirm,
        disabled: isLoading,
        color: 'error',
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDTypography variant="body2" mb={3} color="text.secondary">
            Are you sure you want to delete this line item? This action cannot
            be undone.
          </MDTypography>
        </Grid>

        <Grid item xs={12}>
          <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
            Line Item to Delete
          </MDTypography>
          <MDBox
            p={2}
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
          >
            <DataTable<LineItem>
              table={tableData}
              canSearch={false}
              showTotalEntries={false}
              isSorted={false}
            />
          </MDBox>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default DeleteLineItemModal;
